import { useExternalBookingSettings } from "api/api-hooks/use-external-booking-settings";
import { useWebBooking } from "api/api-hooks/use-web-booking";
import { useBookingId } from "hooks/use-booking-id";
import { useBookingStateContext } from "hooks/use-booking-state";
import { useLocation, useNavigate } from "react-router-dom";
import { Routes } from "routes";

type BookingNavigateParams = {
    fromRoute: Routes;
    back: boolean;
};

export const buildSearchParams = (
    updates: Record<string, string | number | null | undefined>,
    existingSearch: string
): string => {
    const params = new URLSearchParams(existingSearch);

    for (const [key, value] of Object.entries(updates)) {
        if (value === null || value === undefined) {
            params.delete(key);
        } else {
            params.set(key, String(value));
        }
    }

    return `?${params.toString()}`;
};

/**
 * Safely replaces a route segment in a pathname
 * @param pathname - The current pathname
 * @param fromRoute - The route segment to replace
 * @param toRoute - The route segment to replace it with (empty string removes the segment)
 * @returns The updated pathname
 */
const replaceRouteInPathname = (pathname: string, fromRoute: Routes, toRoute: Routes): string => {
    if (!pathname || typeof pathname !== "string") {
        console.error("Invalid pathname provided to replaceRouteInPathname");
        return "/";
    }

    const pathSegments = pathname.split("/").filter((segment) => segment.length > 0);

    const fromRouteString = String(fromRoute);
    const toRouteString = String(toRoute);

    const isFromRouteEmpty = fromRouteString === "";
    const isToRouteEmpty = toRouteString === "";

    if (isFromRouteEmpty) {
        if (!isToRouteEmpty) {
            pathSegments.push(toRouteString);
        }
    } else {
        const fromRouteIndex = pathSegments.findIndex((segment) => segment === fromRouteString);

        if (fromRouteIndex !== -1) {
            if (!isToRouteEmpty) {
                pathSegments[fromRouteIndex] = toRouteString;
            } else {
                pathSegments.splice(fromRouteIndex, 1);
            }
        } else {
            console.warn(
                `Could not find fromRoute "${fromRouteString}" in pathname "${pathname}". ` +
                    `Available segments: [${pathSegments.join(", ")}]`
            );
            return pathname;
        }
    }

    let newPathname = "/" + pathSegments.join("/");

    if (newPathname.length > 1 && newPathname.endsWith("/")) {
        newPathname = newPathname.slice(0, -1);
    }

    return newPathname;
};

export const useBookingNavigate = () => {
    const bookingId = useBookingId();
    const state = useBookingStateContext();
    const {
        guests,
        guestsChildren: children,
        date: dateMoment,
        isActivityBooking,
        isWaitList,
    } = state;
    const date = dateMoment?.format("YYYY-MM-DD") ?? "";
    const navigate = useNavigate();
    const location = useLocation();
    const { data: reservation } = useWebBooking();
    const activateWebChildren =
        useExternalBookingSettings().data?.unitsMetaData?.activateWebChildren;

    const isSimpleBooking = !isActivityBooking;

    // Include menu step if there are menus in the booking and it's not an activity booking
    const includePreorderStep = reservation?.hasMenus() && isSimpleBooking && !isWaitList;

    const bookingNavigate = ({ fromRoute, back = false }: BookingNavigateParams) => {
        let toRoute: Routes = Routes.WIZARD;
        let search: string = location.search;
        let removeBookingId = false;
        let pathname: string = location.pathname;

        let updatedParams: Record<string, string | number | null | undefined> = {
            guests: null,
            children: null,
            date: null,
        };
        if (back) {
            switch (fromRoute) {
                case Routes.WIZARD:
                    if (
                        activateWebChildren &&
                        children !== null &&
                        !location.pathname.includes(Routes.BOOKING_REQUEST)
                    ) {
                        updatedParams = { ...updatedParams, guests };
                    }
                    break;
                case Routes.AVAILABILITY:
                    toRoute = Routes.WIZARD;
                    updatedParams = { ...updatedParams, guests, children };
                    removeBookingId = true;
                    break;
                case Routes.PREORDER:
                    toRoute = Routes.AVAILABILITY;
                    updatedParams = { guests, children, date };
                    removeBookingId = true;
                    break;
                case Routes.FINALIZE:
                    toRoute = Routes.AVAILABILITY;
                    removeBookingId = isWaitList || isSimpleBooking;

                    if (isWaitList || isSimpleBooking) updatedParams = { guests, children, date };

                    if (includePreorderStep) {
                        toRoute = Routes.PREORDER;
                        removeBookingId = false;
                        updatedParams = {};
                    }
                    break;
            }
        }
        updatedParams.withPayment = null;
        search = buildSearchParams(updatedParams, location.search);

        if (fromRoute) {
            pathname = replaceRouteInPathname(pathname, fromRoute, toRoute);
        }

        if (removeBookingId) {
            pathname = pathname.replace(`/${bookingId}`, "");
        }

        void navigate({
            pathname,
            search,
        });
    };

    return { bookingNavigate };
};
